import { useMemo } from 'react';
import memoizeOne from 'memoize-one';
import {
	STORY_POINTS_ALIAS_FIELD_ID,
	STORY_POINT_ESTIMATE_ALIAS_FIELD_ID,
} from '@atlassian/jira-business-constants';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import type { QuickFilterKeys } from '@atlassian/jira-business-filters/src/common/types.tsx';
import { SPRINT_TYPE } from '@atlassian/jira-platform-field-config';
import { useJWMListFeatures } from '../../../controllers/features-context';
import { useIssueFields } from '../../../controllers/get-issue-fields';

const createStringSet = memoizeOne((...args: string[]) => new Set([...args]));

export const useFilterProps = () => {
	const { getAvailableFields } = useIssueFields();
	const { isSoftwareFieldsEnabled } = useJWMListFeatures();

	const availableFields = useMemo(() => getAvailableFields(), [getAvailableFields]);

	// Only hides active sprints if the field is not available or is not software or FF is off
	const hiddenQuickFilters = useMemo<QuickFilterKeys[]>(() => {
		if (!isSoftwareFieldsEnabled) {
			return ['ACTIVE_SPRINTS'];
		}
		const sprintField = availableFields.find((field) => field.type === SPRINT_TYPE);
		if (sprintField == null) {
			return ['ACTIVE_SPRINTS'];
		}
		return [];
	}, [availableFields, isSoftwareFieldsEnabled]);

	const allowedAliasFieldId = useMemo<Set<string> | undefined>(() => {
		if (!isSoftwareFieldsEnabled) {
			return;
		}

		const fieldsAliases: string[] = availableFields
			.filter(
				(field): field is BusinessIssueField & { aliasFieldId: string } =>
					field.aliasFieldId === STORY_POINTS_ALIAS_FIELD_ID ||
					field.aliasFieldId === STORY_POINT_ESTIMATE_ALIAS_FIELD_ID,
			)
			.map((field) => field.aliasFieldId);

		if (fieldsAliases.length === 0) {
			return;
		}
		return createStringSet(...fieldsAliases);
	}, [availableFields, isSoftwareFieldsEnabled]);

	return {
		allowedAliasFieldId,
		hiddenQuickFilters,
	};
};
